import React, { useState } from "react";
import { motion } from "framer-motion";
import KidBackground from "../ui/backgrounds/KidBackground";

const BackgroundDemo = () => {
  const [currentVariant, setCurrentVariant] = useState("aurora");
  const [intensity, setIntensity] = useState(1);

  const variants = [
    { key: "aurora", name: "Aurora Amber", description: "Soft, magical glow - perfect for hero sections" },
    { key: "doodles", name: "Doodle Pattern", description: "Playful hand-drawn elements - great for dashboards" },
    { key: "confetti", name: "Confetti Float", description: "Celebratory shapes - ideal for success screens" },
    { key: "blobs", name: "Blob Parallax", description: "Friendly morphing blobs - perfect for landing pages" },
    { key: "sparkles", name: "Star Sparkles", description: "Twinkling stars - great for galleries" }
  ];

  return (
    <div className="min-h-screen bg-[#FFF8E1] relative">
      <KidBackground variant={currentVariant} intensity={intensity} />
      
      <div className="relative z-10 min-h-screen">
        {/* Header */}
        <motion.header 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 backdrop-blur-sm border-b border-amber-200/50 p-6"
        >
          <div className="max-w-6xl mx-auto">
            <h1 className="text-4xl font-fredoka font-bold text-black mb-2">
              Artmosphere Background System
            </h1>
            <p className="text-lg font-baloo text-gray-700">
              Kid-friendly, amber-themed backgrounds that run at 60fps and respect reduced motion
            </p>
          </div>
        </motion.header>

        {/* Controls */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/90 backdrop-blur-sm m-6 rounded-2xl shadow-[0_10px_30px_rgba(255,193,7,.18)] p-6"
        >
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-fredoka font-bold text-black mb-4">Background Controls</h2>
            
            {/* Variant Selection */}
            <div className="mb-6">
              <label className="block text-lg font-baloo font-medium text-black mb-3">
                Choose Background Variant:
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {variants.map((variant) => (
                  <motion.button
                    key={variant.key}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setCurrentVariant(variant.key)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      currentVariant === variant.key
                        ? "border-amber-500 bg-amber-50 shadow-lg"
                        : "border-amber-200 bg-white hover:border-amber-300"
                    }`}
                  >
                    <div className="text-left">
                      <div className="font-fredoka font-bold text-black">{variant.name}</div>
                      <div className="text-sm font-baloo text-gray-600 mt-1">{variant.description}</div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Intensity Control */}
            <div className="mb-6">
              <label className="block text-lg font-baloo font-medium text-black mb-3">
                Intensity: {intensity.toFixed(1)}
              </label>
              <input
                type="range"
                min="0.1"
                max="2"
                step="0.1"
                value={intensity}
                onChange={(e) => setIntensity(parseFloat(e.target.value))}
                className="w-full h-2 bg-amber-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-sm font-baloo text-gray-600 mt-1">
                <span>Subtle (0.1)</span>
                <span>Normal (1.0)</span>
                <span>Intense (2.0)</span>
              </div>
            </div>

            {/* Usage Examples */}
            <div className="bg-amber-50 rounded-xl p-4">
              <h3 className="text-lg font-fredoka font-bold text-black mb-3">Usage Examples</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm font-baloo">
                <div>
                  <strong className="text-black">Hero/Landing:</strong> "blobs" or "aurora"
                </div>
                <div>
                  <strong className="text-black">Dashboards:</strong> "doodles"
                </div>
                <div>
                  <strong className="text-black">Success/Celebration:</strong> "confetti"
                </div>
                <div>
                  <strong className="text-black">Gallery:</strong> "sparkles"
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Demo Content */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="m-6"
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Sample Cards */}
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * i }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  className="bg-white rounded-2xl shadow-[0_10px_30px_rgba(255,193,7,.18)] p-6"
                >
                  <div className="aspect-square bg-amber-100 rounded-xl mb-4 flex items-center justify-center">
                    <div className="text-4xl">🎨</div>
                  </div>
                  <h3 className="text-xl font-fredoka font-bold text-black mb-2">
                    Sample Card {i}
                  </h3>
                  <p className="text-gray-700 font-baloo">
                    This demonstrates how content looks over the {currentVariant} background with {intensity.toFixed(1)}x intensity.
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Accessibility Info */}
        <motion.footer 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/90 backdrop-blur-sm m-6 rounded-2xl shadow-[0_10px_30px_rgba(255,193,7,.18)] p-6"
        >
          <div className="max-w-6xl mx-auto">
            <h3 className="text-xl font-fredoka font-bold text-black mb-3">Accessibility & Performance</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm font-baloo text-gray-700">
              <div>
                <strong className="text-black">Reduced Motion:</strong> All animations respect prefers-reduced-motion
              </div>
              <div>
                <strong className="text-black">Performance:</strong> 60fps animations using transform/opacity only
              </div>
              <div>
                <strong className="text-black">Contrast:</strong> Backgrounds maintain text readability
              </div>
              <div>
                <strong className="text-black">Lazy Loading:</strong> Backgrounds mount only when needed
              </div>
            </div>
          </div>
        </motion.footer>
      </div>
    </div>
  );
};

export default BackgroundDemo;



